import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { useRerender } from '@react-hookz/web/useRerender/index.js';
import { useTimeoutEffect } from '@react-hookz/web/useTimeoutEffect/index.js';
import { addMilliseconds, differenceInMilliseconds, differenceInMinutes, isAfter, isBefore, isEqual, isSameDay, parseISO, startOfDay, subMilliseconds } from 'date-fns';
import { useMemo } from 'react';
import { useCurrentDate } from '#cutils/context/TimeContext';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import type { LivestreamUpdateQuery } from './__generated__/MediaLivestream.generated';
import { LivestreamUpdateDocument } from './__generated__/MediaLivestream.generated';
type LivestreamStatus = 'NOW_LIVE' | 'FUTURE' | 'PRE_LIVE' | 'PAST';
const ONE_MINUTE_IN_MS = 60_000;
const THEN_MINUTES = 10 * ONE_MINUTE_IN_MS;
const THREE_HOURS_IN_MINUTES = 3 * 60;
const THREE_HOURS_IN_MS = THREE_HOURS_IN_MINUTES * ONE_MINUTE_IN_MS;
const THIRTY_MINUTES_IN_MS = 30 * ONE_MINUTE_IN_MS;
const ONE_HOUR_IN_MS = 60 * ONE_MINUTE_IN_MS;
const FIVE_MINUTES_IN_MS = 5 * ONE_MINUTE_IN_MS;

/**
 * This is the maximum safe timeout value that might be used for `setTimeout`. It is
 * approximately 24 days.
 */
const MAX_SAFE_TIMEOUT = Math.pow(2, 32 - 1) - 1;
function isAfterOrEqual(date: Date, compareDate: Date) {
  return isAfter(date, compareDate) || isEqual(date, compareDate);
}
function isBeforeOrEqual(date: Date, compareDate: Date) {
  return isBefore(date, compareDate) || isEqual(date, compareDate);
}

/**
 * Calculates the livestream status based on start and end time.
 *
 * @param startDate
 * @param endDate
 * @param now
 * @param preliveDuration
 * @returns
 */
function getLivestreamStatus(startDate: Date | null, endDate: Date | null, now: Date, preliveDuration: number): LivestreamStatus | null {
  if (startDate !== null && endDate !== null && isAfterOrEqual(now, startDate) && isBeforeOrEqual(now, endDate)) {
    return 'NOW_LIVE';
  } else if (startDate !== null && isBefore(now, startDate)) {
    if (preliveDuration > 0 && differenceInMilliseconds(now, startDate) <= preliveDuration) {
      return 'PRE_LIVE';
    } else {
      return 'FUTURE';
    }
  } else if (endDate !== null && isAfter(now, endDate)) {
    return 'PAST';
  } else {
    return null;
  }
}

/**
 * Returns undefined if the difference is more than the allowed number for setTimeout.
 *
 * @param firstDate
 * @param secondDate
 */
function getSafeDifferenceInMilliseconds(firstDate: Date, secondDate: Date) {
  const difference = differenceInMilliseconds(firstDate, secondDate);
  if (difference > MAX_SAFE_TIMEOUT || difference < 0) {
    return undefined;
  }
  return difference;
}
function getNextLivestreamLabelTimeout(startDate: Date | null, endDate: Date | null, now: Date, livestreamState: LivestreamStatus | null): number | undefined {
  if (livestreamState === null || livestreamState === 'PAST') {
    return undefined;
  }
  if (livestreamState === 'FUTURE' || livestreamState === 'PRE_LIVE') {
    if (startDate === null) {
      return undefined;
    }
    const timeTillLive = differenceInMilliseconds(startDate, now);
    if (timeTillLive <= THEN_MINUTES) {
      // difference is less than 10 minutes so calling setTimeout with it is safe.
      const timeout = timeTillLive % ONE_MINUTE_IN_MS;
      if (timeout === 0 && timeTillLive > 0) {
        return ONE_MINUTE_IN_MS;
      } else {
        return timeout;
      }
    } else if (isSameDay(startDate, now)) {
      // trigger new state update ten minutes before the start time
      return timeTillLive - THEN_MINUTES;
    } else {
      // trigger new state if the day of the livestream is reached
      return getSafeDifferenceInMilliseconds(startOfDay(startDate), now);
    }
  } else {
    if (endDate === null) {
      return undefined;
    } else {
      // got 1 ms after end date, because the end time still has live as current status
      return getSafeDifferenceInMilliseconds(addMilliseconds(endDate, 1), now);
    }
  }
}

/**
 * Returns the timeout when the next status change of the livestream is due based on the current date.
 *
 * @param startDate the start date of the livestream.
 * @param endDate the end date of the livestream
 * @param now the current date
 * @param preliveDuration the prelive duration window in milliseconds
 * @returns
 */
function getNextLivestreamStatusTimeout(startDate: Date | null, endDate: Date | null, now: Date, preliveDuration: number): number | undefined {
  const status = getLivestreamStatus(startDate, endDate, now, preliveDuration);
  switch (status) {
    case 'FUTURE':
      const difference = getSafeDifferenceInMilliseconds(startDate!, now);
      return difference !== undefined ? difference - preliveDuration : undefined;
    case 'PRE_LIVE':
      return getSafeDifferenceInMilliseconds(startDate!, now);
    case 'NOW_LIVE':
      return getSafeDifferenceInMilliseconds(endDate!, now);
    case 'PAST':
      return undefined;
    default:
      return undefined;
  }
}

/**
 * Returns the current date and livestream status every time the livestream status label should change.
 *
 * @param callback the callback to be called
 * @param status the status of the livestream
 * @param options the start date.
 */
export function useLivestreamLabelCountdown(startDate, endDate) {
  const $ = _c(10);
  const triggerRender = useRerender();
  const currentDate = useCurrentDate();
  let livestreamState;
  let t0;
  if ($[0] !== currentDate || $[1] !== endDate || $[2] !== startDate) {
    livestreamState = getLivestreamStatus(startDate, endDate, currentDate, 0);
    t0 = getNextLivestreamLabelTimeout(startDate, endDate, currentDate, livestreamState);
    $[0] = currentDate;
    $[1] = endDate;
    $[2] = startDate;
    $[3] = livestreamState;
    $[4] = t0;
  } else {
    livestreamState = $[3];
    t0 = $[4];
  }
  const timeout = t0;
  let t1;
  if ($[5] !== triggerRender) {
    t1 = () => {
      triggerRender();
    };
    $[5] = triggerRender;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  useTimeoutEffect(t1, timeout);
  let t2;
  if ($[7] !== currentDate || $[8] !== livestreamState) {
    t2 = {
      currentDate,
      livestreamState
    };
    $[7] = currentDate;
    $[8] = livestreamState;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function mapQueryResult(result: LivestreamUpdateQuery | undefined) {
  const livestream = result?.moduleByRowId?.livestream;
  if (livestream) {
    return {
      startDate: typeof livestream.start === 'string' ? new Date(livestream.start) : null,
      endDate: typeof livestream.end === 'string' ? new Date(livestream.end) : null,
      url: livestream.hls ?? null,
      videos: livestream.programme?.videoFiles?.nodes?.filter(notNullOrUndefined).map(({
        publicLocation
      }) => publicLocation).filter(notNullOrUndefined) ?? []
    };
  } else {
    return undefined;
  }
}
type LivestreamState = {
  startDate: Date | null;
  endDate: Date | null;
  url: string | null;
  videos: string[];
  status: LivestreamStatus | null;
};

/**
 * Returns the poll interval to update the livestream start and end data if they change during the livestream
 * lifecycle.
 *
 * This will return the following configuration:
 * - every 30 minutes if live ist in less than 3 hours or greater than 30 minutes
 * - every 5 minutes if live is in less than 30 minutes.
 * - every 5 minutes for 1 hour if live is in the past and no recordings are available
 *
 *
 * @param livestreamStatus
 * @param startDate
 * @param now
 * @returns
 */
function getPollInterval(livestreamStatus: LivestreamStatus | null, startDate: Date | null, endDate: Date | null, now: Date): {
  pollInterval: number;
  timeout: number | undefined;
} {
  switch (livestreamStatus) {
    case 'FUTURE':
    case 'PRE_LIVE':
      const minutesTillLive = differenceInMinutes(startDate!, now);
      if (minutesTillLive > THREE_HOURS_IN_MINUTES) {
        return {
          pollInterval: 0,
          timeout: getSafeDifferenceInMilliseconds(subMilliseconds(startDate!, THREE_HOURS_IN_MS), now)
        };
      } else if (minutesTillLive > 30) {
        return {
          pollInterval: THIRTY_MINUTES_IN_MS,
          timeout: differenceInMilliseconds(subMilliseconds(startDate!, THIRTY_MINUTES_IN_MS), now)
        };
      } else {
        return {
          pollInterval: FIVE_MINUTES_IN_MS,
          timeout: undefined
        };
      }
    case 'NOW_LIVE':
      return {
        pollInterval: FIVE_MINUTES_IN_MS,
        timeout: undefined
      };
    case 'PAST':
      if (differenceInMinutes(now, endDate!) < 60) {
        return {
          pollInterval: FIVE_MINUTES_IN_MS,
          timeout: differenceInMilliseconds(addMilliseconds(endDate!, ONE_HOUR_IN_MS), now)
        };
      } else {
        return {
          pollInterval: 0,
          timeout: undefined
        };
      }
    default:
      return {
        pollInterval: 0,
        timeout: undefined
      };
  }
}
function getNextTimeout(timeout1: number | undefined, timeout2: number | undefined) {
  if (timeout1 === undefined || timeout2 === undefined) {
    return timeout1 ?? timeout2;
  }
  return Math.min(timeout1, timeout2);
}

/**
 * Hook that updates its state when something regarding the livestream changes.
 *
 * @param rowId
 * @param livestreamStartDate
 * @param livestreamEndDate
 * @param preliveDuration
 * @param url
 * @param videos
 * @returns
 */
export function useLivestreamUpdateStatus(rowId, livestreamStartDate, livestreamEndDate, preliveDuration, url, videos) {
  const $ = _c(36);
  let t0;
  if ($[0] !== livestreamStartDate) {
    t0 = livestreamStartDate ? parseISO(livestreamStartDate) : null;
    $[0] = livestreamStartDate;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const startDate = t0;
  let t1;
  if ($[2] !== livestreamEndDate) {
    t1 = livestreamEndDate ? parseISO(livestreamEndDate) : null;
    $[2] = livestreamEndDate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const endDate = t1;
  const triggerRender = useRerender();
  const currentDate = useCurrentDate();
  let livestreamStatus;
  let pollInterval;
  let t2;
  if ($[4] !== currentDate || $[5] !== endDate || $[6] !== preliveDuration || $[7] !== startDate) {
    const statusTimeout = getNextLivestreamStatusTimeout(startDate, endDate, currentDate, preliveDuration);
    livestreamStatus = getLivestreamStatus(startDate, endDate, currentDate, preliveDuration);
    const {
      pollInterval: t3,
      timeout: pollTimeout
    } = getPollInterval(livestreamStatus, startDate, endDate, currentDate);
    pollInterval = t3;
    t2 = getNextTimeout(statusTimeout, pollTimeout);
    $[4] = currentDate;
    $[5] = endDate;
    $[6] = preliveDuration;
    $[7] = startDate;
    $[8] = livestreamStatus;
    $[9] = pollInterval;
    $[10] = t2;
  } else {
    livestreamStatus = $[8];
    pollInterval = $[9];
    t2 = $[10];
  }
  const timeout = t2;
  let t3;
  if ($[11] !== triggerRender) {
    t3 = () => {
      triggerRender();
    };
    $[11] = triggerRender;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  useTimeoutEffect(t3, timeout);
  const skipQuery = rowId === "rundschau" || videos.length > 0;
  const t4 = rowId as number;
  const t5 = livestreamStatus === "PAST" || livestreamStatus === "NOW_LIVE";
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = {
      rowId: t4,
      withVideoOnDemand: t5
    };
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== pollInterval || $[17] !== skipQuery || $[18] !== t6) {
    t7 = {
      variables: t6,
      skip: skipQuery,
      skipPollAttempt() {
        return skipQuery;
      },
      pollInterval
    };
    $[16] = pollInterval;
    $[17] = skipQuery;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const {
    data,
    error,
    loading,
    previousData
  } = useQuery(LivestreamUpdateDocument, t7);
  if (loading || error) {
    let t8;
    if ($[20] !== previousData) {
      t8 = mapQueryResult(previousData);
      $[20] = previousData;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    const result = t8;
    if (result) {
      let t9;
      if ($[22] !== livestreamStatus || $[23] !== result) {
        t9 = {
          ...result,
          status: livestreamStatus
        };
        $[22] = livestreamStatus;
        $[23] = result;
        $[24] = t9;
      } else {
        t9 = $[24];
      }
      return t9;
    }
  } else {
    let t8;
    if ($[25] !== data) {
      t8 = mapQueryResult(data);
      $[25] = data;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    const result_0 = t8;
    if (result_0) {
      let t9;
      if ($[27] !== livestreamStatus || $[28] !== result_0) {
        t9 = {
          ...result_0,
          status: livestreamStatus
        };
        $[27] = livestreamStatus;
        $[28] = result_0;
        $[29] = t9;
      } else {
        t9 = $[29];
      }
      return t9;
    }
  }
  let t8;
  if ($[30] !== endDate || $[31] !== livestreamStatus || $[32] !== startDate || $[33] !== url || $[34] !== videos) {
    t8 = {
      startDate,
      endDate,
      url,
      videos,
      status: livestreamStatus
    };
    $[30] = endDate;
    $[31] = livestreamStatus;
    $[32] = startDate;
    $[33] = url;
    $[34] = videos;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
}